import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Star, Heart, Filter, Trees, Eye, Building2, UtensilsCrossed, Mountain, Landmark, Users, Sunset, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEOHead from '../components/SEOHead';
import WalkCard from '../components/WalkCard';
import WalkCarousel from '../components/WalkCarousel';
import { mockWalks, categories, getUserFavorites } from '../data/mock';
import { generateLocalBusinessSchema } from '../utils/seoData';
import formatDuration from '../utils/formatDuration';
import categoryLabel, { distanceCategory } from '../utils/categoryLabels';

const HomePage = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [featuredWalks, setFeaturedWalks] = useState([]);
  const [recentWalks, setRecentWalks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch featured and recent walks from API
  useEffect(() => {
    const fetchWalks = async () => {
      try {
        setLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        
        // Fetch featured walks
        const featuredResponse = await fetch(`${backendUrl}/api/walks/featured`);
        const featuredData = await featuredResponse.json();
        
        // Fetch recent walks
        const recentResponse = await fetch(`${backendUrl}/api/walks/recent`);
        const recentData = await recentResponse.json();
        
        if (featuredResponse.ok && recentResponse.ok) {
          // Transform API data to match component expectations
          const transformWalk = (walk) => ({
            id: walk.id,
            slug: walk.slug,
            country: walk.country || 'us',
            city: walk.city || 'los-angeles',
            neighborhood: walk.neighborhood || '',
            title: walk.title,
            description: walk.description,
            practicalSummary: walk.practical_summary || walk.description,
            startingPoint: walk.starting_point,
            endingPoint: walk.ending_point,
            distance: walk.distance_meters ? `${(walk.distance_meters / 1609.34).toFixed(1)} mi` : null,
            duration: formatDuration(walk.duration_minutes),
            difficulty: walk.difficulty,
            distanceCategory: walk.distance_category || distanceCategory(walk.distance_meters),
            category: categoryLabel(walk.categories?.[0]) || 'Urban Walk',
            region: walk.region,
            photos: walk.images || [walk.main_image],
            highlights: walk.highlights || [],
            featured: walk.featured || false,
            userRatings: walk.user_ratings || 0,
            ratingCount: walk.rating_count || 0,
            completionCount: walk.completion_count || 0,
          });
          
          setFeaturedWalks(featuredData.walks.map(transformWalk));
          setRecentWalks(recentData.walks.map(transformWalk));
        } else {
          // Fallback to mock data if API fails
          setFeaturedWalks(mockWalks.slice(0, 3));
          setRecentWalks(mockWalks.slice(3, 6));
        }
      } catch (err) {
        console.error('Error fetching walks:', err);
        setError(err.message);
        // Fallback to mock data
        setFeaturedWalks(mockWalks.slice(0, 3));
        setRecentWalks(mockWalks.slice(3, 6));
      } finally {
        setLoading(false);
      }
    };

    fetchWalks();
  }, []);

  const handleFavoriteToggle = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Curated Urban Walks in Los Angeles - Mindful Walking Routes"
        description="Discover the best scenic urban walks, hiking trails, and walking routes in Los Angeles. Curated guides for mindful exploration, wellness on foot, and discovering hidden gems in LA."
        keywords="Los Angeles walks, LA hiking trails, urban walks Los Angeles, scenic walks LA, walking routes Los Angeles, best walks in LA, Los Angeles walking tours, urban hiking LA"
        structuredData={generateLocalBusinessSchema()}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-gray-900 to-black text-white">
        <div className="absolute inset-0 bg-black/30"></div>
        <div 
          className="relative min-h-screen flex items-center justify-center bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1580655653885-65763b2597d0?w=1200&h=800&fit=crop')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
          <div className="relative z-10 text-center max-w-4xl mx-auto px-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 backdrop-blur-sm">
              <div className="w-8 h-8 border-2 border-white rounded-full"></div>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Discover Your City's
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
                Hidden Walks
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-2xl mx-auto leading-relaxed">
              Curated urban adventures that reveal the soul of your neighborhood, one step at a time.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/walks">
                <Button size="lg" className="bg-white text-black hover:bg-gray-100 px-8 py-3 text-lg font-semibold">
                  Explore Walks
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Walks Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-16">
            <div>
              <h2 className="text-4xl font-bold mb-4">Featured Walks</h2>
              <p className="text-xl text-gray-600">
                Hand-picked adventures from cities around the world
              </p>
            </div>
            <Link to="/walks">
              <Button variant="outline" size="lg" className="hidden md:flex items-center gap-2">
                View All Walks
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={`loading-${index}`} className="animate-pulse">
                  <div className="bg-gray-200 aspect-[4/3] rounded-lg mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (
            <WalkCarousel>
              {featuredWalks.map((walk) => (
                <WalkCard
                  key={`featured-${walk.id}-${refreshKey}`}
                  walk={walk}
                  onFavoriteToggle={handleFavoriteToggle}
                />
              ))}
            </WalkCarousel>
          )}
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Walk Categories</h2>
            <p className="text-xl text-gray-600">
              Find walks that match your interests and mood
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((category) => {
              // Define unique icons for each category
              const getCategoryIcon = (categoryName) => {
                switch (categoryName) {
                  case 'Nature & Parks':
                    return <Trees className="h-6 w-6 text-white" />;
                  case 'Hidden Gems':
                    return <Eye className="h-6 w-6 text-white" />;
                  case 'Architecture':
                    return <Building2 className="h-6 w-6 text-white" />;
                  case 'Food-Focused Walks':
                    return <UtensilsCrossed className="h-6 w-6 text-white" />;
                  case 'Scenic Walks':
                    return <Mountain className="h-6 w-6 text-white" />;
                  case 'Historic Walks':
                    return <Landmark className="h-6 w-6 text-white" />;
                  case 'Family-Friendly':
                    return <Users className="h-6 w-6 text-white" />;
                  case 'Sunset Walks':
                    return <Sunset className="h-6 w-6 text-white" />;
                  default:
                    return <MapPin className="h-6 w-6 text-white" />;
                }
              };

              return (
                <Link 
                  key={category} 
                  to={`/walks?categories=${category.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '')}`}
                  className="group"
                >
                  <Card className="text-center hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    <CardContent className="p-6">
                      <div className="h-12 w-12 mx-auto mb-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        {getCategoryIcon(category)}
                      </div>
                      <h3 className="font-semibold group-hover:text-amber-600 transition-colors">
                        {category}
                      </h3>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recent Walks Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-16">
            <div>
              <h2 className="text-4xl font-bold mb-4">Recently Added</h2>
              <p className="text-xl text-gray-600">
                Fresh discoveries and new adventures
              </p>
            </div>
            <Link to="/walks">
              <Button variant="outline" size="lg" className="hidden md:flex items-center gap-2">
                View All Walks
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={`loading-recent-${index}`} className="animate-pulse">
                  <div className="bg-gray-200 aspect-[4/3] rounded-lg mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (
            <WalkCarousel>
              {recentWalks.map((walk) => (
                <WalkCard
                  key={`recent-${walk.id}-${refreshKey}`}
                  walk={walk}
                  onFavoriteToggle={handleFavoriteToggle}
                />
              ))}
            </WalkCarousel>
          )}
          <div className="text-center mt-12 md:hidden">
            <Link to="/walks">
              <Button variant="outline" size="lg" className="flex items-center gap-2">
                View All Walks
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIzIi8+PC9nPjwvZz48L3N2Zz4=')]"></div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Join the Wanderloop Community</h2>
          <p className="text-xl mb-8 text-blue-100">
            Share your favorite walks and help fellow explorers discover hidden gems in their city
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/walks">
              <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold px-8 py-3 text-lg">
                Start Exploring
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/submit">
              <Button size="lg" variant="outline" className="bg-transparent text-white border-white hover:bg-white/10 font-bold px-8 py-3 text-lg border-2">
                Submit Your Walk
                <Plus className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Floating Actions */}
      <FloatingActions />
      
      
      <Footer />
    </div>
  );
};

export default HomePage;